import React, { useState, useEffect, useRef } from 'react';
import airportsData from '../assets/airports.json';
import './FlightModal.css';

const FlightModal = ({ show, onClose }) => {
    const [step, setStep] = useState(1);
    const formRef = useRef();
    // State for passenger counts
    const [adults, setAdults] = useState(1);
    const [children, setChildren] = useState(0);
    // State for passenger details
    const [adultForms, setAdultForms] = useState([{ title: '', firstName: '', lastName: '' }]);
    const [childForms, setChildForms] = useState([]);

    // Autocomplete state for From/To
    const [fromQuery, setFromQuery] = useState('');
    const [toQuery, setToQuery] = useState('');
    const [fromResults, setFromResults] = useState([]);
    const [toResults, setToResults] = useState([]);
    const [fromSelected, setFromSelected] = useState(''); // display label
    const [toSelected, setToSelected] = useState(''); // display label
    const [fromIata, setFromIata] = useState(''); // actual value
    const [toIata, setToIata] = useState(''); // actual value
    const [showFromDropdown, setShowFromDropdown] = useState(false);
    const [showToDropdown, setShowToDropdown] = useState(false);
    // Helper: flatten airports object to array
    const airportsArr = React.useMemo(() => Object.values(airportsData).filter(a => a.iata && a.iata.trim() !== ''), []);
    // State for form fields to avoid direct DOM access
    const [tripTypeState, setTripTypeState] = useState('roundtrip');
    const [flightClassState, setFlightClassState] = useState('economy');
    const [departDateState, setDepartDateState] = useState('');
    const [returnDateState, setReturnDateState] = useState('');
    const [contactNameState, setContactNameState] = useState('');
    const [contactEmailState, setContactEmailState] = useState('');
    const [contactPhoneState, setContactPhoneState] = useState('');
    // Autocomplete filter logic
    useEffect(() => {
        if (fromQuery.length < 2) {
            setFromResults([]);
            return;
        }
        const q = fromQuery.toLowerCase();
        setFromResults(
            airportsArr.filter(a =>
                (a.city && a.city.toLowerCase().includes(q)) ||
                (a.name && a.name.toLowerCase().includes(q)) ||
                (a.iata && a.iata.toLowerCase().includes(q))
            ).slice(0, 8)
        );
    }, [fromQuery, airportsArr]);

    useEffect(() => {
        if (toQuery.length < 2) {
            setToResults([]);
            return;
        }
        const q = toQuery.toLowerCase();
        setToResults(
            airportsArr.filter(a =>
                (a.city && a.city.toLowerCase().includes(q)) ||
                (a.name && a.name.toLowerCase().includes(q)) ||
                (a.iata && a.iata.toLowerCase().includes(q))
            ).slice(0, 8)
        );
    }, [toQuery, airportsArr]);

    // Multi-step form handling (vanilla JS logic)
    // Update passenger form state when adults/children changes
    useEffect(() => {
        setAdultForms(Array.from({ length: adults }, (_, i) => adultForms[i] || { title: '', firstName: '', lastName: '' }));
        setChildForms(Array.from({ length: children }, (_, i) => childForms[i] || { title: '', firstName: '', lastName: '', age: '' }));
        // eslint-disable-next-line
    }, [adults, children]);

    // Validation logic for steps
    function validateStep(step) {
        if (step === 1) {
            // ...existing code...
            return true;
        } else if (step === 2) {
            // Validate adult forms
            for (let i = 0; i < adults; i++) {
                const a = adultForms[i];
                if (!a.title || !a.firstName || !a.lastName) {
                    alert('Please fill in all passenger information');
                    return false;
                }
            }
            // Validate child forms
            for (let i = 0; i < children; i++) {
                const c = childForms[i];
                if (!c.title || !c.firstName || !c.lastName || !c.age) {
                    alert('Please fill in all passenger information');
                    return false;
                }
                const age = parseInt(c.age);
                if (isNaN(age) || age < 2 || age > 11) {
                    alert('Child age must be between 2 and 11 years');
                    return false;
                }
            }
        }
        return true;
    }


    if (!show) return null;



    async function handleSubmit(e) {
        e.preventDefault();
        // Use state for all values
        const passengers = [
            ...adultForms.map(a => ({
                type: 'Adult',
                title: a.title,
                firstName: a.firstName,
                lastName: a.lastName
            })),
            ...childForms.map(c => ({
                type: 'Child',
                title: c.title,
                firstName: c.firstName,
                lastName: c.lastName,
                age: c.age
            }))
        ];
        const payload = {
            tripType: tripTypeState,
            fromCity: fromIata,
            toCity: toIata,
            departDate: departDateState,
            returnDate: returnDateState,
            passengers,
            class: flightClassState,
            contact: {
                name: contactNameState,
                email: contactEmailState,
                phone: contactPhoneState
            }
        };
        try {
            const response = await fetch('https://sedertravel.sa/api/book-flight', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                onClose();
            } else {
                alert('Failed to submit booking. Please try again.');
            }
        } catch (err) {
            alert('An error occurred. Please try again.');
        }
    }
  return (
        <div
            id="flightModal"
            className={`flight-modal${show ? ' show' : ''}`}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
        >
            <div className="modal-content modern-modal">
                <span className="close-modal" onClick={onClose} tabIndex={0} role="button" aria-label="Close Modal">&times;</span>
                <h2 className="modal-title">Book Your Flight</h2>
                <form id="flightBookingForm" className="modern-form" onSubmit={handleSubmit}>
                    {step === 1 && (
                        <div className="form-step" id="step1">
                            <div className="form-group">
                                <label htmlFor="tripType">Trip Type</label>
                                <div className="custom-select-wrapper">
                                    <select
                                        id="tripType"
                                        required
                                        className="custom-select custom-select-arrow-fix"
                                        value={tripTypeState}
                                        onChange={e => setTripTypeState(e.target.value)}
                                    >
                                        <option value="roundtrip">Round Trip</option>
                                        <option value="oneway">One Way</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="class">From</label>
                                    <div className="autocomplete-wrapper">
                                        <input
                                            type="text"
                                            id="fromCity"
                                            placeholder="Departure City"
                                            required
                                            autoComplete="off"
                                            value={fromSelected || fromQuery}
                                            onChange={e => {
                                                setFromQuery(e.target.value);
                                                setFromSelected('');
                                                setFromIata('');
                                                setShowFromDropdown(true);
                                            }}
                                            onFocus={() => setShowFromDropdown(true)}
                                            onBlur={() => setTimeout(() => setShowFromDropdown(false), 150)}
                                        />
                                        {showFromDropdown && fromResults.length > 0 && (
                                            <div className="autocomplete-results" id="fromCity-results">
                                                {fromResults.map((a, idx) => (
                                                    <div
                                                        key={a.iata + a.name + idx}
                                                        className="autocomplete-item"
                                                        onMouseDown={() => {
                                                            setFromSelected(`${a.name}, ${a.city} (${a.iata})`);
                                                            setFromQuery(`${a.name}, ${a.city} (${a.iata})`);
                                                            setFromIata(a.iata);
                                                            setShowFromDropdown(false);
                                                        }}
                                                    >
                                                        <span className="airport-main">{a.name}</span>
                                                        <span className="airport-city">{a.city}</span>
                                                        <span className="airport-iata">{a.iata}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="class">To</label>
                                    <div className="autocomplete-wrapper">
                                        <input
                                            type="text"
                                            id="toCity"
                                            placeholder="Destination City"
                                            required
                                            autoComplete="off"
                                            value={toSelected || toQuery}
                                            onChange={e => {
                                                setToQuery(e.target.value);
                                                setToSelected('');
                                                setToIata('');
                                                setShowToDropdown(true);
                                            }}
                                            onFocus={() => setShowToDropdown(true)}
                                            onBlur={() => setTimeout(() => setShowToDropdown(false), 150)}
                                        />
                                        {showToDropdown && toResults.length > 0 && (
                                            <div className="autocomplete-results" id="toCity-results">
                                                {toResults.map((a, idx) => (
                                                    <div
                                                        key={a.iata + a.name + idx}
                                                        className="autocomplete-item"
                                                        onMouseDown={() => {
                                                            setToSelected(`${a.name}, ${a.city} (${a.iata})`);
                                                            setToQuery(`${a.name}, ${a.city} (${a.iata})`);
                                                            setToIata(a.iata);
                                                            setShowToDropdown(false);
                                                        }}
                                                    >
                                                        <span className="airport-main">{a.name}</span>
                                                        <span className="airport-city">{a.city}</span>
                                                        <span className="airport-iata">{a.iata}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                            </div>
 


                                <div className="form-row form-row-dates">
                                <div className="form-group">
                                    <label htmlFor="departDate">Departure Date</label>
                                    <input
                                        type="date"
                                        id="departDate"
                                        required
                                        value={departDateState}
                                        onChange={e => setDepartDateState(e.target.value)}
                                    />
                                </div>
                                <div className="form-group return-date">
                                    <label htmlFor="returnDate">Return Date</label>
                                    <input
                                        type="date"
                                        id="returnDate"
                                        value={returnDateState}
                                        onChange={e => setReturnDateState(e.target.value)}
                                    />
                                </div>
                            </div>
                                                        <div className="form-group">
                                <label htmlFor="class">Class</label>
                                <div className="custom-select-wrapper">
                                    <select
                                        id="class"
                                        required
                                        className="custom-select custom-select-arrow-fix"
                                        value={flightClassState}
                                        onChange={e => setFlightClassState(e.target.value)}
                                    >
                                        <option value="economy">Economy</option>
                                        <option value="business">Business</option>
                                        <option value="first">First Class</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="passengers">Number of Passengers</label>
                                <div className="passenger-selector modern-passenger-selector">
                                    <div className="custom-select-wrapper">
                                        <select id="adults" required value={adults} onChange={e => setAdults(Number(e.target.value))} className="custom-select custom-select-arrow-fix">
                                            <option value="1">1 Adult</option>
                                            <option value="2">2 Adults</option>
                                            <option value="3">3 Adults</option>
                                            <option value="4">4 Adults</option>
                                        </select>

                                    </div>
                                    <div className="custom-select-wrapper">
                                        <select id="children" value={children} onChange={e => setChildren(Number(e.target.value))} className="custom-select custom-select-arrow-fix">
                                            <option value="0">No Children</option>
                                            <option value="1">1 Child</option>
                                            <option value="2">2 Children</option>
                                            <option value="3">3 Children</option>
                                        </select>

                                    </div>
                                </div>
                            </div>
                            <button type="button" className="next-button modern-btn" onClick={() => {
                                // Validate step 1 before moving to step 2
                                if (validateStep(1)) setStep(2);
                            }}>Next</button>
                        </div>
                    )}
                    {step === 2 && (
                        <div className="form-step" id="step2">
                            <div id="passengerForms">
                                {/* Render adult forms */}
                                {adultForms.map((adult, i) => (
                                    <div className="passenger-form modern-passenger-form" key={`adult${i}`}>
                                        <h4>Adult {i + 1}</h4>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor={`adult${i + 1}Title`}>Title</label>
                                                <div className="custom-select-wrapper">
                                                    <select id={`adult${i + 1}Title`} required value={adult.title} onChange={e => {
                                                        const updated = [...adultForms];
                                                        updated[i].title = e.target.value;
                                                        setAdultForms(updated);
                                                    }} className="custom-select custom-select-arrow-fix">
                                                        <option value="">Select</option>
                                                        <option value="Mr">Mr</option>
                                                        <option value="Mrs">Mrs</option>
                                                        <option value="Ms">Ms</option>
                                                    </select>
            
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor={`adult${i + 1}FirstName`}>First Name</label>
                                                <input type="text" id={`adult${i + 1}FirstName`} required value={adult.firstName} onChange={e => {
                                                    const updated = [...adultForms];
                                                    updated[i].firstName = e.target.value;
                                                    setAdultForms(updated);
                                                }} />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor={`adult${i + 1}LastName`}>Last Name</label>
                                                <input type="text" id={`adult${i + 1}LastName`} required value={adult.lastName} onChange={e => {
                                                    const updated = [...adultForms];
                                                    updated[i].lastName = e.target.value;
                                                    setAdultForms(updated);
                                                }} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {/* Render child forms */}
                                {childForms.map((child, i) => (
                                    <div className="passenger-form modern-passenger-form" key={`child${i}`}>
                                        <h4>Child {i + 1}</h4>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor={`child${i + 1}Title`}>Title</label>
                                                <div className="custom-select-wrapper">
                                                    <select id={`child${i + 1}Title`} required value={child.title} onChange={e => {
                                                        const updated = [...childForms];
                                                        updated[i].title = e.target.value;
                                                        setChildForms(updated);
                                                    }} className="custom-select custom-select-arrow-fix">
                                                        <option value="">Select</option>
                                                        <option value="Mr">Mr</option>
                                                        <option value="Mrs">Mrs</option>
                                                        <option value="Ms">Ms</option>
                                                        <option value="Master">Master</option>
                                                    </select>
            
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor={`child${i + 1}FirstName`}>First Name</label>
                                                <input type="text" id={`child${i + 1}FirstName`} required value={child.firstName} onChange={e => {
                                                    const updated = [...childForms];
                                                    updated[i].firstName = e.target.value;
                                                    setChildForms(updated);
                                                }} />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor={`child${i + 1}LastName`}>Last Name</label>
                                                <input type="text" id={`child${i + 1}LastName`} required value={child.lastName} onChange={e => {
                                                    const updated = [...childForms];
                                                    updated[i].lastName = e.target.value;
                                                    setChildForms(updated);
                                                }} />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor={`child${i + 1}Age`}>Age</label>
                                                <input type="number" id={`child${i + 1}Age`} min="2" max="11" required value={child.age} onChange={e => {
                                                    const updated = [...childForms];
                                                    updated[i].age = e.target.value;
                                                    setChildForms(updated);
                                                }} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="button-group modern-btn-group">
                                <button type="button" className="back-button modern-btn" onClick={() => setStep(1)}>Back</button>
                                <button type="button" className="next-button modern-btn" onClick={() => {
                                    if (validateStep(2)) setStep(3);
                                }}>Next</button>
                            </div>
                        </div>
                    )}
                    {step === 3 && (
                        <div className="form-step" id="step3">
                            <div className="form-group">
                                <label htmlFor="contactName">Full Name</label>
                                <input
                                    type="text"
                                    id="contactName"
                                    required
                                    value={contactNameState}
                                    onChange={e => setContactNameState(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="contactEmail">Email</label>
                                <input
                                    type="email"
                                    id="contactEmail"
                                    required
                                    value={contactEmailState}
                                    onChange={e => setContactEmailState(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="contactPhone">Mobile Number</label>
                                <input
                                    type="tel"
                                    id="contactPhone"
                                    required
                                    inputMode="tel"
                                    pattern="^((\+|00)?[1-9][0-9]{7,14})$"
                                    maxLength="13"
                                    placeholder="e.g. +966512345678"
                                    value={contactPhoneState}
                                    onChange={e => setContactPhoneState(e.target.value.replace(/[^\d+]/g, ''))}
                                />
                                <small style={{color:'#888',fontSize:'0.97em'}}>Enter a valid mobile number (with country code, e.g. +966...)</small>
                            </div>
                            <div className="button-group modern-btn-group">
                                <button type="submit" className="submit-button modern-btn">Submit Booking</button>
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </div>
  );
};

export default FlightModal;
